import { useState, useEffect, useCallback, useRef } from "react";
import { useAuth } from "@/contexts/AuthContext";
import api from "@/lib/api";
import { toast } from "sonner";
import { AUTO_SAVE_INTERVAL_MS } from "@/lib/constants";
import { formatFieldValue, formatFormData, formatDateIST, formatTimeIST } from "@/lib/formatUtils";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Plus, Search, FileEdit } from "lucide-react";
import DraftSection from "@/components/shared/DraftSection";
import ExportButtons from "@/components/shared/ExportButtons";
import EntryDetailDialog from "@/components/shared/EntryDetailDialog";
import logger from "@/lib/logger";
import OtherValueDialog from "@/components/shared/OtherValueDialog";
import PartyAutocomplete from "@/components/shared/PartyAutocomplete";
const COLUMNS = [
  { key: "ref_no", label: "Ref No." },
  { key: "party_name", label: "Party Name" },
  { key: "invoice_no", label: "Invoice No." },
  { key: "material_description", label: "Material" },
  { key: "quantity", label: "Qty" },
  { key: "vehicle_no", label: "Vehicle" },
  { key: "driver_name", label: "Driver" },
  { key: "created_at", label: "Date" },
];

export default function Dispatch() {
  const [entries, setEntries] = useState([]);
  const [total, setTotal] = useState(0);
  const [showForm, setShowForm] = useState(false);
  const [search, setSearch] = useState("");
  const [detailEntry, setDetailEntry] = useState(null);
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [parties, setParties] = useState([]);
  const [vehicleTypes, setVehicleTypes] = useState([]);
  const [drafts, setDrafts] = useState([]);
  const [currentDraftId, setCurrentDraftId] = useState(null);
  const [lastAutoSave, setLastAutoSave] = useState(null);
  const [otherField, setOtherField] = useState(null);
  const [otherValue, setOtherValue] = useState("");
  const autoSaveRef = useRef(null);
  const { location } = useAuth();

  const fetchEntries = useCallback(async () => {
    try {
      const { data } = await api.get("/mod4/dispatch", { params: { location, search: search || undefined } });
      setEntries(data.entries || []);
      setTotal(data.total || 0);
    } catch (err) { logger.error("Failed to fetch entries:", err); }
  }, [location, search]);

  const fetchDrafts = useCallback(async () => {
    try {
      const { data } = await api.get("/drafts/dispatch", { params: { location } });
      setDrafts(data.drafts || []);
    } catch (err) { logger.error("Failed to fetch drafts:", err); }
  }, [location]);

  useEffect(() => { fetchEntries(); fetchDrafts(); }, [fetchEntries, fetchDrafts]);

  const loadMasters = useCallback(async () => {
    try {
      const [p, v] = await Promise.all([api.get("/masters/parties"), api.get("/masters/vehicle_types")]);
      setParties(p.data.items);
      setVehicleTypes(v.data.items);
    } catch (err) { logger.error("Failed to load masters:", err); }
  }, []);

  useEffect(() => { loadMasters(); }, [loadMasters]);

  // Auto-save every 30s — use refs to avoid interval recreation on every keystroke
  const formRef = useRef(form);
  formRef.current = form;
  const draftIdRef = useRef(currentDraftId);
  draftIdRef.current = currentDraftId;

  useEffect(() => {
    if (!showForm) return;
    autoSaveRef.current = setInterval(async () => {
      const f = formRef.current;
      if (!f || Object.keys(f).length === 0) return;
      try {
        const payload = { ...f, location, _draft_id: draftIdRef.current || undefined };
        const { data } = await api.post("/drafts/dispatch", payload);
        if (!draftIdRef.current && data.id) setCurrentDraftId(data.id);
        setLastAutoSave(new Date());
      } catch (err) { logger.error("Auto-save failed:", err); }
    }, AUTO_SAVE_INTERVAL_MS);
    return () => clearInterval(autoSaveRef.current);
  }, [showForm, location]);

  const handleSaveDraft = async () => {
    try {
      const payload = { ...form, location, _draft_id: currentDraftId || undefined };
      const { data } = await api.post("/drafts/dispatch", payload);
      if (!currentDraftId && data.id) setCurrentDraftId(data.id);
      toast.success("Draft saved");
      setLastAutoSave(new Date());
      fetchDrafts();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
  };

  const handleSubmit = async () => {
    if (!form.party_name?.trim()) { toast.error("Party Name is required"); return; }
    if (!form.vehicle_no?.trim()) { toast.error("Vehicle No. is required"); return; }
    if (!form.invoice_no?.trim()) { toast.error("Invoice No. is required"); return; }
    setSaving(true);
    try {
      await api.post("/mod4/dispatch", { ...form, location });
      toast.success("Dispatch entry created");
      if (currentDraftId) await api.post(`/drafts/dispatch/${currentDraftId}/submit`).catch(() => {});
      closeForm();
      fetchEntries();
      fetchDrafts();
    } catch (err) { toast.error(err.response?.data?.detail || "Error"); }
    setSaving(false);
  };

  const handleOpenDraft = async (draft) => {
    try {
      const { data } = await api.get(`/drafts/dispatch/${draft.id}`);
      setForm(data.data || {});
      setCurrentDraftId(draft.id);
      setShowForm(true);
    } catch (err) { toast.error(err.response?.data?.detail || "Cannot open draft"); }
  };

  const handleDeleteDraft = async (draftId) => {
    try { await api.delete(`/drafts/dispatch/${draftId}`); toast.success("Draft deleted"); fetchDrafts(); } catch { toast.error("Failed"); }
  };

  const handleSelectOrOther = (fieldKey, val, masterType) => {
    if (val === "__other__") { setOtherField({ masterType, fieldKey }); return; }
    setField(fieldKey, val);
  };

  const fetchMasters = () => {
    Promise.all([api.get("/masters/parties"), api.get("/masters/vehicle_types")]).then(([p, v]) => {
      setParties(p.data.items);
      setVehicleTypes(v.data.items);
    }).catch((err) => { logger.error("Failed to refresh masters:", err); });
  };

  const handleOtherSubmit = async () => {
    if (!otherValue.trim() || !otherField) return;
    const val = formatFieldValue(otherField.fieldKey, otherValue.trim());
    setField(otherField.fieldKey, val);
    try { await api.post(`/masters/${otherField.masterType}`, { name: val }); } catch (err) { logger.error("Failed to save master:", err); }
    fetchMasters();
    toast.success(`"${val}" added`);
    setOtherField(null); setOtherValue("");
  };

  const closeForm = () => {
    setShowForm(false);
    if (currentDraftId) api.post(`/drafts/dispatch/${currentDraftId}/release-lock`).catch(() => {});
    setCurrentDraftId(null);
    setLastAutoSave(null);
    setForm({});
  };

  const setField = (k, v) => setForm(prev => ({ ...prev, [k]: formatFieldValue(k, v) }));

  return (
    <div className="space-y-5" data-testid="dispatch-page">
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-2">
        <div>
          <h1 className="text-xl sm:text-2xl font-bold" style={{ fontFamily: "Chivo" }}>Dispatch / Outgoing</h1>
          <p className="text-xs sm:text-sm text-gray-500">MOD-4 · Sales dispatches & outgoing material</p>
        </div>
        <Button onClick={() => { setForm({}); setCurrentDraftId(null); setShowForm(true); }} className="bg-[#1B3C87] hover:bg-[#2650A8] h-10 sm:h-11 text-xs sm:text-sm w-full sm:w-auto" data-testid="new-entry-btn">
          <Plus size={18} className="mr-1" /> New Dispatch
        </Button>
      </div>

      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-2">
        <ExportButtons reportType="dispatch_register" title="Dispatch Register" entries={entries} columns={COLUMNS} location={location} />
        <div className="relative w-full sm:w-auto">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <Input placeholder="Search..." value={search} onChange={e => setSearch(e.target.value)} className="pl-9 h-9 w-full sm:w-52" data-testid="search-input" />
        </div>
      </div>

      <DraftSection drafts={drafts} onOpen={handleOpenDraft} onDelete={handleDeleteDraft} draftType="dispatch" />

      <Card>
        <Table>
          <TableHeader>
            <TableRow className="bg-[#1B3C87] hover:bg-[#1B3C87]">
              {COLUMNS.map(h => (
                <TableHead key={h.key} className="text-white text-xs uppercase tracking-wider font-bold">{h.label}</TableHead>
              ))}
            </TableRow>
          </TableHeader>
          <TableBody>
            {entries.length === 0 && <TableRow><TableCell colSpan={8} className="text-center py-8 text-gray-400">No entries</TableCell></TableRow>}
            {entries.map(e => (
              <TableRow key={e.id} className="hover:bg-blue-50/50 cursor-pointer" onClick={() => setDetailEntry(e)}>
                <TableCell className="font-mono text-xs">{e.ref_no}</TableCell>
                <TableCell className="font-medium">{e.party_name || "\u2014"}</TableCell>
                <TableCell className="text-sm">{e.invoice_no || "\u2014"}</TableCell>
                <TableCell className="text-sm">{e.material_description || "\u2014"}</TableCell>
                <TableCell className="text-sm">{e.quantity || "\u2014"}{e.uom ? ` ${e.uom}` : ""}</TableCell>
                <TableCell className="text-sm">{e.vehicle_no || "\u2014"}</TableCell>
                <TableCell className="text-sm">{e.driver_name || "\u2014"}</TableCell>
                <TableCell className="text-xs">{e.created_at ? formatDateIST(e.created_at) : "\u2014"}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        <div className="p-3 border-t text-xs text-gray-500">{total} total entries</div>
      </Card>

      <Dialog open={showForm} onOpenChange={(open) => { if (!open) closeForm(); }}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <div className="flex items-center justify-between">
              <DialogTitle style={{ fontFamily: "Chivo" }}>
                {currentDraftId ? <span className="flex items-center gap-2"><FileEdit size={16} className="text-amber-600" /> Continue Draft</span> : "New Dispatch Entry"}
              </DialogTitle>
              {lastAutoSave && <span className="text-[10px] text-gray-400">Draft saved {formatTimeIST(lastAutoSave)}</span>}
            </div>
          </DialogHeader>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="space-y-1.5 sm:col-span-2">
              <PartyAutocomplete
                value={form.party_name || ""}
                onChange={(v) => setField("party_name", v)}
                label="Party Name *"
                testId="field-party-autocomplete"
                masterType="parties"
              />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs uppercase tracking-wider font-bold text-gray-500">Invoice No. *</Label>
              <Input value={form.invoice_no || ""} onChange={e => setField("invoice_no", e.target.value)} className="h-11" data-testid="field-invoice" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs uppercase tracking-wider font-bold text-gray-500">Invoice Date</Label>
              <Input type="date" value={form.invoice_date || ""} onChange={e => setField("invoice_date", e.target.value)} className="h-11" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs uppercase tracking-wider font-bold text-gray-500">Material Description</Label>
              <Input value={form.material_description || ""} onChange={e => setField("material_description", e.target.value)} className="h-11" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs uppercase tracking-wider font-bold text-gray-500">Quantity</Label>
              <Input type="number" value={form.quantity || ""} onChange={e => setField("quantity", e.target.value)} className="h-11" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs uppercase tracking-wider font-bold text-gray-500">Weight (Kg)</Label>
              <Input type="number" value={form.weight || ""} onChange={e => setField("weight", e.target.value)} className="h-11" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs uppercase tracking-wider font-bold text-gray-500">Vehicle No.</Label>
              <Input value={form.vehicle_no || ""} onChange={e => setField("vehicle_no", e.target.value)} className="h-11" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs uppercase tracking-wider font-bold text-gray-500">Vehicle Type</Label>
              <Select value={form.vehicle_type || ""} onValueChange={v => handleSelectOrOther("vehicle_type", v, "vehicle_types")}>
                <SelectTrigger className="h-11"><SelectValue placeholder="Select type" /></SelectTrigger>
                <SelectContent>
                  {vehicleTypes.map(p => <SelectItem key={p.id} value={p.name}>{p.name}</SelectItem>)}
                  <SelectItem value="__other__" className="text-amber-600 font-semibold">Other (not in list)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs uppercase tracking-wider font-bold text-gray-500">Driver Name</Label>
              <Input value={form.driver_name || ""} onChange={e => setField("driver_name", e.target.value)} className="h-11" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs uppercase tracking-wider font-bold text-gray-500">Driver Mobile</Label>
              <Input value={form.driver_mobile || ""} onChange={e => setField("driver_mobile", e.target.value)} className="h-11" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs uppercase tracking-wider font-bold text-gray-500">LR No.</Label>
              <Input value={form.lr_no || ""} onChange={e => setField("lr_no", e.target.value)} className="h-11" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs uppercase tracking-wider font-bold text-gray-500">Transporter</Label>
              <Input value={form.transporter || ""} onChange={e => setField("transporter", e.target.value)} className="h-11" />
            </div>
          </div>
          <DialogFooter className="flex-col sm:flex-row gap-2">
            <Button variant="outline" onClick={closeForm} className="h-11">Cancel</Button>
            <Button variant="outline" onClick={handleSaveDraft} className="h-11 border-amber-300 text-amber-700 hover:bg-amber-50" data-testid="save-draft-btn">
              <FileEdit size={14} className="mr-1" /> Save Draft
            </Button>
            <Button onClick={handleSubmit} disabled={saving} className="bg-[#1B3C87] hover:bg-[#2650A8] h-11" data-testid="save-entry-btn">
              {saving ? "Saving..." : "Submit Entry"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* "Other" value dialog */}
      <OtherValueDialog
        otherField={otherField}
        otherValue={otherValue}
        setOtherValue={setOtherValue}
        onSubmit={handleOtherSubmit}
        onClose={() => { setOtherField(null); setOtherValue(""); }}
      />

      <EntryDetailDialog open={!!detailEntry} onClose={() => setDetailEntry(null)} entry={detailEntry} title="Dispatch Details" collection="dispatches" onRefresh={fetchEntries} />
    </div>
  );
}
